// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@store/MobiusOverrides/Select/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import objectValues from "@store/Framework/Common/Utilities/objectValues";
import { OrderChangeRequestLineModel, OrderChangeRequestLineStateModel } from "@store/Framework/Types/ApiModels";
import TextField, { TextFieldPresentationProps } from "@store/MobiusOverrides/TextField/TextField";
import getRequiredFieldMessage from "@toolkit/Framework/Common/Utilities/forms/getRequiredFieldMessage";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import UndoFill from "@store/Library/Components/Toolkit/Undo/UndoFill";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";
import { ProductLineStatusType } from "@store/Library/Components/ProductLine/ProductLineStatusOverlay";

export const orderChangeRequestLineReasons = {
    newProduct: "New Product",
    cancellation: "Cancellation",
    dateChange: "Date Change",
    deliveryAppointmentTimeChanges: "Delivery Appointment Time Changes",
    quantityChange: "Quantity Change",
    productChange: "Product Change",
    purchaseOrderChange: "Purchase Order Change",
    deliveryAddressChange: "Delivery Address Change",
};

export const orderChangeRequestLineCanceledState: Partial<OrderChangeRequestLineStateModel> = {
    isCanceled: true,
    changeReason: orderChangeRequestLineReasons.cancellation,
    newQuantity: "",
};

export const orderChangeRequestLineRestoredState: Partial<OrderChangeRequestLineStateModel> = {
    isCanceled: false,
    changeReason: "",
    newQuantity: "",
};

export const orderChangeRequestLineDefaultState: Partial<OrderChangeRequestLineStateModel> = {
    isCanceled: false,
    changeReason: "",
    changeNotes: "",
    newQuantity: "",
};

export interface OrderChangeRequestLineFormProps {
    lineState: OrderChangeRequestLineStateModel;
    status?: ProductLineStatusType;
    onUpdate: (lineStateUpdate: Partial<OrderChangeRequestLineStateModel>) => void;
}

export interface OrderChangeRequestLineFormStyles {
    wrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    reasonSelect?: SelectPresentationProps;
    notesTextField?: TextFieldPresentationProps;
    quantityTextField?: TextFieldPresentationProps;
    undoLink?: LinkPresentationProps;
}

const orderChangeRequestLineFormStyles: OrderChangeRequestLineFormStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(16, 24)};
        `,
    },
    reasonSelect: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                flex-grow: 1;
                flex-basis: ${pxToRem(320)};
            `,
        },
    },
    notesTextField: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                flex-grow: 999999;
                flex-basis: ${pxToRem(320)};
            `,
        },
    },
    quantityTextField: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                flex-basis: 0;
            `,
            inputSelect: css`
                min-width: ${pxToRem(100)};
            `,
        },
        labelProps: {
            css: css`
                ${x => x.theme.formField.defaultProps?.labelProps?.css}
                white-space: nowrap;
            `,
        },
    },
    undoLink: mergeProps<LinkPresentationProps>(linkGetActionLinkProps(UndoFill), {
        css: css`
            align-self: flex-end;
        `,
    }),
};

const styles = orderChangeRequestLineFormStyles;

const OrderChangeRequestLineForm: FC<OrderChangeRequestLineFormProps> = ({ lineState, status, onUpdate }) => {
    const [reason, setReason] = useState(lineState?.changeReason ?? "");
    const [notes, setNotes] = useState(lineState?.changeNotes ?? "");
    const [quantity, setQuantity] = useState(`${Number(lineState?.newQuantity) || ""}`);

    useEffect(() => {
        if (lineState) {
            setReason(lineState.changeReason ?? "");
            setNotes(lineState.changeNotes ?? "");
            setQuantity(`${Number(lineState.newQuantity) || ""}`);
            validateLine(lineState, true);
        }
    }, [lineState?.changeReason, lineState?.changeNotes, lineState?.newQuantity]);

    const reasonLabel = translate("Change Request Reason");

    const getIsReasonRequired = (notesValue = notes, quantityValue = quantity) => {
        return Boolean(notesValue || quantityValue);
    };

    const getReasonErrorMessage = (notesValue = notes, quantityValue = quantity, reasonValue = reason) => {
        return getIsReasonRequired(notesValue, quantityValue) && !reasonValue
            ? getRequiredFieldMessage(reasonLabel)
            : "";
    };

    const getIsQuantityRequired = (reasonValue = reason) => {
        return [orderChangeRequestLineReasons.quantityChange, orderChangeRequestLineReasons.newProduct].includes(
            reasonValue,
        );
    };

    const getQuantityErrorMessage = (quantityValue = quantity, reasonValue = reason) => {
        const isQuantityRequired = getIsQuantityRequired(reasonValue);

        if (isQuantityRequired || quantityValue) {
            const number = quantityValue ? parseInt(quantityValue, 10) : 0;

            if (number < 1) {
                return translate("Quantity must be a minimum of 1.");
            }
        }

        if (
            isQuantityRequired &&
            lineState.originalQuantity &&
            quantityValue === lineState.originalQuantity.toString()
        ) {
            return translate("Same as current QTY");
        }

        return "";
    };

    const validateLine = (line: Partial<OrderChangeRequestLineModel>, update?: boolean) => {
        const { changeNotes, changeReason } = line;

        const newQuantity =
            typeof line.newQuantity === "number" ? `${line.newQuantity}` : (line.newQuantity ?? quantity);

        const hasError =
            !!getReasonErrorMessage(changeNotes, newQuantity, changeReason) ||
            !!getQuantityErrorMessage(newQuantity, changeReason);

        if (update && lineState && lineState.hasError !== hasError) {
            onUpdate({ hasError });
        }

        return hasError;
    };

    if (!lineState) {
        return null;
    }

    const updateLine = (lineStateUpdate: Partial<OrderChangeRequestLineStateModel>) => {
        onUpdate({ ...lineStateUpdate, hasError: validateLine(lineStateUpdate) });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.formWrapper}>
                <Select
                    {...styles.reasonSelect}
                    data-test-selector="OrderChangeRequest_reasonSelect"
                    disabled={
                        reason === orderChangeRequestLineReasons.newProduct ||
                        status === "canceled" ||
                        lineState?.isCanceled
                    }
                    error={getReasonErrorMessage()}
                    label={reasonLabel}
                    onChange={event => {
                        const changeReason = event.target.value;
                        setReason(changeReason);

                        const isCanceled = changeReason === orderChangeRequestLineReasons.cancellation;
                        const wasCanceled = reason === orderChangeRequestLineReasons.cancellation;

                        if (isCanceled) {
                            updateLine(orderChangeRequestLineCanceledState);
                        } else if (wasCanceled) {
                            updateLine({ ...orderChangeRequestLineRestoredState, changeReason });
                        } else {
                            updateLine({ changeReason, isCanceled });
                        }
                    }}
                    required={getIsReasonRequired()}
                    value={reason}
                >
                    <option value="">{translate("Select a reason...")}</option>
                    {objectValues(orderChangeRequestLineReasons)?.map(reason => {
                        if (reason === orderChangeRequestLineReasons.newProduct) {
                            if (status !== "added") {
                                return null;
                            }
                        } else if (status === "added") {
                            return null;
                        }

                        return (
                            <option value={reason} key={reason}>
                                {translate(`${reason}`)}
                            </option>
                        );
                    })}
                </Select>
                <TextField
                    {...styles.notesTextField}
                    data-test-selector="OrderChangeRequest_notes"
                    label={translate("Change Request Note")}
                    onBlur={() => updateLine({ changeNotes: notes })}
                    onChange={event => setNotes(event.target.value)}
                    placeholder={siteMessage("OrderChangeRequest_NotesPlaceholder").toString()}
                    value={notes}
                />
                <TextField
                    {...styles.quantityTextField}
                    data-test-selector="OrderChangeRequest_Qty"
                    disabled={status === "canceled"}
                    error={getQuantityErrorMessage()}
                    label={translate("New QTY")}
                    onBlur={() => updateLine({ newQuantity: quantity, changeReason: reason })}
                    onChange={event => {
                        const quantity = event.target.value;
                        setQuantity(quantity);

                        if (quantity && !reason) {
                            setReason(orderChangeRequestLineReasons.quantityChange);
                        } else if (!quantity && reason === orderChangeRequestLineReasons.quantityChange) {
                            setReason("");
                        }
                    }}
                    placeholder={translate("QTY")}
                    required={getIsQuantityRequired()}
                    type="number"
                    value={quantity}
                />
            </StyledWrapper>
            {status === "modified" && (
                <Link
                    {...styles.undoLink}
                    data-test-selector="OrderChangeRequest_undoBtn"
                    onClick={() => updateLine(orderChangeRequestLineDefaultState)}
                >
                    {translate("Undo changes")}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default OrderChangeRequestLineForm;
